import React, { useState, useEffect, useMemo } from "react";
import {
    addCSRProject,
    getCSRProjects,
    getCSRProjectById,
    editCSRProject,
    deleteCSRProject,
} from "../../controllers/csrProjects.controller";
import toast from 'react-hot-toast';
import {
    IconPlus,
} from "@tabler/icons-react";
import { RiListRadio, RiDeleteBin2Line } from "react-icons/ri";
import { FiEdit } from "react-icons/fi";
import Button from "../../Components/Button";
import Page from "../../Components/Page";

const emptyFormData = {
    project_name: "",
    description: "",
    images: [],
    links: [],
};

const CSRProjects = () => {

    const [isOpen, setIsOpen] = useState(false);
    const [data, setData] = useState([]);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [toggeledIndex_1, setToggeledIndex] = useState(0);

    const [formData, setFormData] = useState(emptyFormData);

    // Existing images kept as parallel arrays of filename + preview url
    const [existingImagePaths, setExistingImagePaths] = useState([]);
    const [existingImageUrls, setExistingImageUrls] = useState([]);

    const [toggles, setToggles] = useState([
        { label: 'Add New', icon: <IconPlus />, isToggled: false },
        { label: 'List', icon: <RiListRadio />, isToggled: true },
    ]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
    };

    // ---- Links handlers ----
    const addLink = () => {
        setFormData(prevState => ({
            ...prevState,
            links: [...prevState.links, { title: "", url: "" }],
        }));
    };

    const handleLinkChange = (index, field, value) => {
        setFormData(prevState => {
            const newLinks = [...prevState.links];
            newLinks[index] = { ...newLinks[index], [field]: value };
            return { ...prevState, links: newLinks };
        });
    };

    const removeLink = (index) => {
        setFormData(prevState => {
            const newLinks = [...prevState.links];
            newLinks.splice(index, 1);
            return { ...prevState, links: newLinks };
        });
    };

    // Keep only links with at least a title or url
    const sanitizeLinks = (links) =>
        (links || []).filter(link => link && (link.title?.trim() || link.url?.trim()));

    const fetchTableData = async () => {
        const res = await getCSRProjects()

        if (res.status === 200) {
            const apiData = res?.data?.data;
            setData(apiData || [])
        }
    }

    useEffect(() => {
        fetchTableData()
    }, [])

    // add
    const addNewCSRProject = async () => {
        try {
            const { project_name, description, images, links } = formData;

            if (!project_name || !description || images.length === 0) {
                toast.error("Please fill in all the required fields and upload at least one image.");
                return;
            }

            toast.loading("Submitting your data...");

            const formDataToSend = new FormData();

            formDataToSend.append("project_name", project_name);
            formDataToSend.append("description", description);
            images.forEach(image => {
                formDataToSend.append("file", image);
            });
            formDataToSend.append("links", JSON.stringify(sanitizeLinks(links)));

            const res = await addCSRProject(formDataToSend);

            const message = res.data.message;
            toast.dismiss();

            if (res.status === 200) {
                toast.success(message);
                setFormData(emptyFormData);
                setToggeledIndex(1);
                handleToggle(1);
                fetchTableData()
            } else {
                toast.error(message || "Failed to add data.");
            }
        } catch (error) {
            toast.dismiss();
            console.error("Error adding data:", error);
            const message = error?.response?.data?.error || error?.response?.data?.message || "Something went wrong!";
            toast.error(message);
        }
    };

    const removeExistingImage = (index) => {
        setExistingImagePaths(prevState => {
            const newPaths = [...prevState];
            newPaths.splice(index, 1);
            return newPaths;
        });
        setExistingImageUrls(prevState => {
            const newUrls = [...prevState];
            newUrls.splice(index, 1);
            return newUrls;
        });
    };

    // open edit modal (fetch full record by id)
    const openEditModal = async (id) => {
        try {
            setSelectedRowId(id);
            toast.loading("Loading...");
            const res = await getCSRProjectById(id);
            toast.dismiss();

            if (res.status === 200) {
                const record = res?.data?.data || {};

                setFormData({
                    project_name: record.project_name || "",
                    description: record.description || "",
                    images: [],
                    links: Array.isArray(record.links) ? record.links : [],
                });

                setExistingImagePaths(Array.isArray(record.image_path) ? record.image_path : []);
                setExistingImageUrls(Array.isArray(record.image_urls) ? record.image_urls : []);

                document.getElementById("modal-edit").showModal();
            } else {
                toast.error("Failed to load project details.");
            }
        } catch (error) {
            toast.dismiss();
            console.error("Error loading project:", error);
            const message = error?.response?.data?.message || "Something went wrong!";
            toast.error(message);
        }
    };

    // edit
    const editCSRBtn = async () => {
        try {
            const { project_name, description, images, links } = formData;

            if (!project_name || !description) {
                toast.error("Please fill in all the required fields.");
                return;
            }

            toast.loading("Submitting your data...");

            const formDataToSend = new FormData();

            formDataToSend.append("project_name", project_name);
            formDataToSend.append("description", description);

            if (images && images.length > 0) {
                images.forEach(image => {
                    formDataToSend.append("file", image);
                });
            }

            // Append existing image filenames to keep, as a JSON string
            formDataToSend.append("existing_images", JSON.stringify(existingImagePaths));
            formDataToSend.append("links", JSON.stringify(sanitizeLinks(links)));

            const res = await editCSRProject(selectedRowId, formDataToSend);

            const message = res.data.message;
            toast.dismiss();

            if (res.status === 200) {
                toast.success(message);
                setFormData(emptyFormData);
                setExistingImagePaths([]);
                setExistingImageUrls([]);
                setSelectedRowId(null);

                document.getElementById("modal-edit").close()
                fetchTableData()
            } else {
                toast.error(message || "Failed to update data.");
            }
        } catch (error) {
            toast.dismiss();
            document.getElementById("modal-edit").close()
            console.error("Error updating data:", error);
            const message = error?.response?.data?.error || error?.response?.data?.message || "Something went wrong!";
            toast.error(message);
        }
    };


    // delete
    const deleteCSRBtn = async () => {
        try {
            if (selectedRowId) {
                toast.loading("Please wait...");
                const res = await deleteCSRProject(selectedRowId)
                const message = res.data.message;
                toast.dismiss();
                if (res.status === 200) {
                    toast.success(message);
                    fetchTableData()
                    setSelectedRowId(null)
                    document.getElementById("modal-delete").close()
                } else {
                    toast.error(message);
                }
            }
        } catch (error) {
            console.error(error);
            const message = error?.response?.data?.error || "Something went wrong!";
            toast.dismiss();
            toast.error(message);
        }
    }

    const handleImageChange = (event) => {
        const files = Array.from(event.target.files);
        setFormData(prevState => ({
            ...prevState,
            images: [...prevState.images, ...files],
        }));
    };

    //Function to remove image from array
    const removeImage = (index) => {
        setFormData(prevState => {
            const newImages = [...prevState.images];
            newImages.splice(index, 1);
            return { ...prevState, images: newImages };
        });
    };

    const handleToggle = (index) => {
        setToggles((prevToggles) =>
            prevToggles.map((toggle, i) => ({ ...toggle, isToggled: i === index }))
        );
    };

    const toggeledIndex = useMemo(() => {
        return toggles.findIndex((d) => d.isToggled)
    }, [toggles])

    // Reusable links editor
    const renderLinksEditor = () => (
        <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
                <h1 className="block text-black font-medium text-[18px]">Links</h1>
                <button
                    type="button"
                    onClick={addLink}
                    className="px-3 py-1 bg-[#92288C14] text-[#92288C] border border-[#92288C] rounded-md flex items-center gap-1"
                >
                    <IconPlus size={16} /> Add Link
                </button>
            </div>
            {formData.links.length === 0 && (
                <p className="text-sm text-gray-400">No links added.</p>
            )}
            {formData.links.map((link, index) => (
                <div key={index} className="flex flex-col gap-2 mb-3 md:flex-row md:items-center">
                    <input
                        type="text"
                        placeholder="Title (e.g. Facebook)"
                        value={link.title || ""}
                        onChange={(e) => handleLinkChange(index, "title", e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm md:w-1/3"
                    />
                    <input
                        type="text"
                        placeholder="URL (e.g. https://facebook.com/example)"
                        value={link.url || ""}
                        onChange={(e) => handleLinkChange(index, "url", e.target.value)}
                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm md:flex-1"
                    />
                    <button
                        type="button"
                        onClick={() => removeLink(index)}
                        className="px-3 py-2 text-red-500 border border-red-200 rounded-md"
                    >
                        Remove
                    </button>
                </div>
            ))}
        </div>
    );

    return (
        <Page className="px-8 py-6">
            <div className='flex items-center gap-10 text-xl'>
                <div className="text-[#100f13]">
                    Research & Student Life / Student Life /
                    <span className="text-[black] bg-white mt-6"> CSR Projects</span>
                </div>
            </div>
            <div className="flex flex-wrap gap-4 mt-4">
                {toggles.map((toggle, index) => (
                    <div key={index} className="flex flex-col items-center">

                        <Button styles={'h-100'} outlined={!toggle.isToggled} onClick={() => handleToggle(index)}>
                            <span className="flex flex-row items-center justify-center w-[150px]">{toggle.icon} {toggle.label}</span>
                        </Button>

                    </div>

                ))}
            </div>

            <div className="w-full p-6 mx-auto mt-10 bg-white rounded-lg shadow-lg">
                {toggeledIndex === 1 ? (
                    <div>
                        <h1 className="text-xl font-bold mb-2 text-[#4B465C] mt-6">CSR Projects List</h1>

                        <table className="w-full bg-white border-collapse rounded-lg shadow-md">
                            <thead>
                                <tr className="bg-gray-100 text-[#4B465C]">
                                    <th className="w-1/3 px-4 py-2 text-center">Project Name</th>
                                    <th className="w-1/3 px-4 py-2 text-center">Description</th>
                                    <th className="w-1/3 px-4 py-2 text-center">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.map((item) => (
                                    <tr key={item.id} className="border-b hover:bg-gray-50">

                                        <td className="px-4 py-2 text-center">{item.project_name}</td>

                                        <td className="px-4 py-2 text-center">
                                            <span className="line-clamp-2">{item.description}</span>
                                        </td>

                                        <td className="px-4 py-2 text-center">
                                            <div className="flex justify-center gap-4">
                                                <button
                                                    onClick={() => {
                                                        setSelectedRowId(item.id)
                                                        document.getElementById("modal-delete").showModal();
                                                    }}
                                                    className="flex items-center px-3 py-1 bg-[#A8AAAE14] text-[#A8AAAE] border border-[#A8AAAE] rounded-md hover:bg-gray-300"
                                                >
                                                    Delete
                                                    <RiDeleteBin2Line className="ml-2" />
                                                </button>

                                                <button onClick={() => openEditModal(item.id)} className="px-3 py-1 bg-[#92288C14] text-[#92288C] border border-[#92288C] rounded-md flex items-center gap-1">
                                                    Edit
                                                    <FiEdit />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {data.length === 0 && (
                                    <tr>
                                        <td colSpan={3} className="px-4 py-6 text-center text-gray-400">
                                            No CSR projects found.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="container">
                        <div className="p-6 mx-auto bg-white rounded-lg shadow-md">
                            <form onSubmit={(e) => e.preventDefault()}>
                                <h1 className="mb-4 text-xl font-bold text-gray-700">Add CSR Project Details</h1>

                                {/* Project Name Input */}
                                <div className="mb-4">
                                    <label htmlFor="project_name" className="block mb-2 text-gray-600">
                                        Project Name
                                    </label>
                                    <input
                                        type="text"
                                        id="project_name"
                                        name="project_name"
                                        value={formData.project_name}
                                        onChange={handleChange}
                                        className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm"
                                    />
                                </div>


                                <div className="w-full mb-4 text-left">
                                    <label className="block mb-1 font-medium text-gray-600">
                                        Description
                                    </label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        value={formData.description}
                                        onChange={handleChange}
                                        rows="4"
                                        className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:[#92288C]"
                                    />
                                </div>

                                <div className="mb-6">
                                    <h1 className="block text-black font-medium mb-2 text-[18px]">
                                        Images <b className="text-[#4B465C]">(Accept: .jpg, .png)</b>
                                    </h1>
                                    <div className="flex flex-col">
                                        <label className="px-4 py-2 text-[#92288C] rounded-md cursor-pointer border-2 border-[#92288C] hover:text-[#92288C] hover:bg-[#92288C14] w-fit">
                                            <input
                                                type="file"
                                                accept=".jpg, .png"
                                                multiple
                                                onChange={handleImageChange}
                                                className="hidden"
                                            />
                                            Browse Images
                                        </label>
                                        {formData.images.length > 0 && (
                                            <div className="mt-2">
                                                {formData.images.map((image, index) => (
                                                    <div key={index} className="flex items-center justify-between py-2 border-b">
                                                        <span>{image.name}</span>
                                                        <button
                                                            type="button"
                                                            onClick={() => removeImage(index)}
                                                            className="text-red-500"
                                                        >
                                                            Remove
                                                        </button>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {renderLinksEditor()}

                                <div className="flex justify-end space-x-4">
                                    <button
                                        className="px-4 py-2 text-gray-500 border border-gray-300 rounded-md"
                                        onClick={() => {
                                            setFormData(emptyFormData);
                                            setToggeledIndex(1);
                                            handleToggle(1);
                                        }}
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="button"
                                        onClick={addNewCSRProject}
                                        className="px-4 py-2 bg-[#92288C] text-white rounded-md hover:bg-[#7a2175]"
                                    >
                                        Add
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>

                )
                }
            </div>
            <dialog
                id="modal-edit"
                className="modal modal-bottom xl:modal-middle inset-0 z-50 rounded-lg w-[600px]"
            >
                <div className="p-6">
                    <h1 className="mb-6 text-2xl font-bold">Edit CSR Project</h1>
                    <form onSubmit={(e) => e.preventDefault()}>

                        <div className="mb-4">
                            <label htmlFor="project_name" className="block mb-2 text-gray-600">
                                Project Name
                            </label>
                            <input
                                type="text"
                                id="project_name"
                                name="project_name"
                                value={formData.project_name}
                                onChange={handleChange}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm"
                            />
                        </div>

                        <div className="w-full mb-4 text-left">
                            <div>
                                <label className="block mb-1 font-medium text-gray-600"> Description</label>

                                <textarea
                                    id="description"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    rows="4"
                                    className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:[#92288C]"
                                />
                            </div>
                        </div>

                        <div className="mb-6">
                            <h1 className="block text-black font-medium mb-2 text-[18px]">
                                Images <b className="text-[#4B465C]">(Accept: .jpg, .png)</b>
                            </h1>
                            <div className="flex flex-col">
                                <label className="px-4 py-2 text-[#92288C] rounded-md cursor-pointer border-2 border-[#92288C] hover:text-[#92288C] hover:bg-[#92288C14] w-fit">
                                    <input
                                        type="file"
                                        accept=".jpg, .png"
                                        multiple
                                        onChange={handleImageChange}
                                        className="hidden"
                                    />
                                    Browse Images
                                </label>

                                {/* Display existing images */}
                                {existingImagePaths.length > 0 && (
                                    <div className="mt-2">
                                        <p className="mb-2 font-semibold">Current Images:</p>
                                        <div className="flex flex-wrap gap-3">
                                            {existingImagePaths.map((path, index) => (
                                                <div key={index} className="relative flex flex-col items-center">
                                                    {existingImageUrls[index] ? (
                                                        <img
                                                            src={existingImageUrls[index]}
                                                            alt={path}
                                                            className="object-cover w-20 h-20 border rounded-md"
                                                        />
                                                    ) : (
                                                        <span className="text-xs break-all w-20">{path}</span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => removeExistingImage(index)}
                                                        className="mt-1 text-xs text-red-500"
                                                    >
                                                        Remove
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {formData.images.length > 0 && (
                                    <div className="mt-2">
                                        {formData.images.map((image, index) => (
                                            <div key={index} className="flex items-center justify-between py-2 border-b">
                                                <span>{image.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeImage(index)}
                                                    className="text-red-500"
                                                >
                                                    Remove
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {renderLinksEditor()}

                        <div className="flex justify-end space-x-4">

                            <button
                                className="px-4 py-2 text-gray-500 border border-gray-300 rounded-md"
                                onClick={() => {
                                    setSelectedRowId(null)
                                    setFormData(emptyFormData);
                                    setExistingImagePaths([]);
                                    setExistingImageUrls([]);
                                    document.getElementById("modal-edit").close()
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => editCSRBtn()}
                                className="px-4 py-2 bg-[#92288C] text-white rounded-md hover:bg-[#7a2175] justify-end"
                            >
                                save
                            </button>


                        </div>
                    </form>
                </div>
            </dialog>

            <dialog
                id="modal-delete"
                className="inset-0 z-50 rounded-lg modal modal-bottom sm:modal-middle"
            >

                <div className="relative z-10 w-full max-w-lg p-6 mx-auto bg-white rounded-lg shadow-lg">
                    <div className="text-center">
                        <h2 className="text-xl font-bold text-gray-900">
                            Delete
                        </h2>
                        <p className="font-bold text-gray-500">
                            Are you sure?
                        </p>
                    </div>


                    <div className="flex justify-center mt-4 space-x-4">
                        <button
                            onClick={() => {
                                setSelectedRowId(null)
                                document.getElementById("modal-delete").close()
                            }}
                            className="px-6 py-3 text-gray-600 transition bg-gray-200 rounded-lg hover:bg-gray-200 active:scale-95 hover:shadow-lg"
                        >
                            No
                        </button>
                        <button
                            onClick={async () => { deleteCSRBtn(); }}
                            className="rounded-lg transition active:scale-95 hover:shadow-lg px-6 py-3 bg-[#92288C] text-white"
                        >
                            Yes
                        </button>
                    </div>
                </div>
            </dialog>

        </Page>

    );
};

export default CSRProjects;
